import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import api from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import {
  Users,
  UserPlus,
  MoreHorizontal,
  Mail,
  Shield,
  ShieldCheck,
  Trash2,
  Edit,
  Search,
  Plus,
  Pencil,
  KeyRound,
  Eye,
  EyeOff,
  Clock,
  RefreshCw,
  X,
  MessageSquare,
  Users2
} from 'lucide-react'
import { PageHeader } from '@/components/dashboard-ui'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { MobileListItem } from '@/components/ui/MobileListItem'
import MobileFAB from '@/components/layout/MobileFAB'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'

interface TeamMember {
  id: string
  name: string
  email: string
  role: 'admin' | 'supervisor' | 'agent'
  // 'employee' (default) | 'agency_partner' — agency partners are people
  // who set up the org on the firm's behalf via a partner access code.
  // Surfaced in the Team UI so the firm sees who's actually a member of
  // the firm vs. a setup helper from a marketing/agency relationship.
  relationship?: 'employee' | 'agency_partner'
  status: 'active' | 'inactive' | 'online' | 'offline' | 'busy' | 'away'
  avatar?: string
  lastLoginAt?: string
  lastActive?: string
  createdAt: string
}

interface User {
  id: string
  email: string
  name: string
  role: 'admin' | 'supervisor' | 'agent'
  status: 'online' | 'offline' | 'busy' | 'away'
  department?: string
  avatarUrl?: string
  lastActive?: string
  createdAt: string
}

interface Invitation {
  id: string
  email: string
  role: string
  invitedBy: string
  expiresAt: string
  acceptedAt: string | null
  createdAt: string
  status: 'pending' | 'accepted' | 'expired'
}

interface Organization {
  id: string
  name: string
  slug: string
  limits: {
    agents: number
  }
}

export default function TeamPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const statusFilter = searchParams.get('filter') // 'online' or null
  const { agent } = useAuth()
  const { toast } = useToast()

  // Team Members State
  const [members, setMembers] = useState<TeamMember[]>([])
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showInviteDialog, setShowInviteDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [activeTab, setActiveTab] = useState('members')
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null)
  const [inviteForm, setInviteForm] = useState<{ email: string; name: string; role: 'admin' | 'supervisor' | 'agent' }>({ email: '', name: '', role: 'agent' })
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Invitations State
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [invitationsLoading, setInvitationsLoading] = useState(true)

  // User Accounts State
  const [users, setUsers] = useState<User[]>([])
  const [usersLoading, setUsersLoading] = useState(true)
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isEditUserDialogOpen, setIsEditUserDialogOpen] = useState(false)
  const [isDeleteUserDialogOpen, setIsDeleteUserDialogOpen] = useState(false)
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'agent',
    department: ''
  })

  useEffect(() => {
    loadTeam()
    loadOrganization()
    loadInvitations()
  }, [])

  const loadOrganization = async () => {
    try {
      const response = await api.get('/api/organizations/current')
      if (response.data.success) {
        setOrganization(response.data.organization)
      }
    } catch (error) {
      console.error('Failed to load organization:', error)
    }
  }

  const loadTeam = async () => {
    try {
      const response = await api.get('/api/organizations/current')
      if (response.data.success) {
        const orgId = response.data.organization.id
        const membersResponse = await api.get(`/api/organizations/${orgId}/members`)
        if (membersResponse.data.success) {
          setMembers(membersResponse.data.members)
        }
      }
    } catch (error) {
      console.error('Failed to load team members:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const loadInvitations = async () => {
    try {
      const response = await api.get('/api/organizations/current')
      if (response.data.success) {
        const orgId = response.data.organization.id
        const invitesResponse = await api.get(`/api/organizations/${orgId}/invitations`)
        if (invitesResponse.data.success) {
          setInvitations(invitesResponse.data.invitations)
        }
      }
    } catch (error) {
      console.error('Failed to load invitations:', error)
    } finally {
      setInvitationsLoading(false)
    }
  }

  const handleResendInvite = async (inviteId: string) => {
    if (!organization) return
    try {
      const response = await api.post(`/api/organizations/${organization.id}/invitations/${inviteId}/resend`)
      if (response.data.success) {
        toast({ title: 'Invitation resent', description: response.data.message })
        loadInvitations()
      }
    } catch (error: any) {
      toast({
        title: 'Failed to resend',
        description: error.response?.data?.message || 'Something went wrong',
        variant: 'destructive'
      })
    }
  }

  const handleCancelInvite = async (inviteId: string) => {
    if (!organization) return
    try {
      const response = await api.delete(`/api/organizations/${organization.id}/invitations/${inviteId}`)
      if (response.data.success) {
        toast({ title: 'Invitation cancelled', description: response.data.message })
        loadInvitations()
      }
    } catch (error: any) {
      toast({
        title: 'Failed to cancel',
        description: error.response?.data?.message || 'Something went wrong',
        variant: 'destructive'
      })
    }
  }

  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (roleFilter !== 'all') params.append('role', roleFilter)
      if (userSearchQuery) params.append('search', userSearchQuery)

      const response = await api.get(`/api/admin/users?${params.toString()}`)

      if (response.data.success) {
        setUsers(response.data.users)
      }
    } catch (error) {
      console.error('Error fetching users:', error)
      toast({
        title: 'Error',
        description: 'Failed to load users',
        variant: 'destructive'
      })
    } finally {
      setUsersLoading(false)
    }
  }, [roleFilter, userSearchQuery, toast])

  useEffect(() => {
    if (agent?.role === 'admin') {
      fetchUsers()
    }
  }, [fetchUsers, agent?.role])

  // Team Member handlers
  const handleInviteMember = async () => {
    if (!organization) return

    setIsSubmitting(true)
    try {
      const response = await api.post(`/api/organizations/${organization.id}/members`, inviteForm)
      if (response.data.success) {
        toast({
          title: 'Invitation sent',
          description: response.data.message || `An invitation has been sent to ${inviteForm.email}`
        })
        setShowInviteDialog(false)
        setInviteForm({ email: '', name: '', role: 'agent' })
        loadTeam()
        loadInvitations()
      }
    } catch (error: any) {
      toast({
        title: 'Failed to invite member',
        description: error.response?.data?.message || 'Something went wrong',
        variant: 'destructive'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUpdateRole = async (memberId: string, newRole: string) => {
    if (!organization) return

    try {
      await api.put(`/api/organizations/${organization.id}/members/${memberId}`, { role: newRole })
      toast({
        title: 'Role updated',
        description: 'Team member role has been updated'
      })
      loadTeam()
    } catch (error: any) {
      toast({
        title: 'Failed to update role',
        description: error.response?.data?.message || 'Something went wrong',
        variant: 'destructive'
      })
    }
  }

  const handleRemoveMember = async () => {
    if (!organization || !selectedMember) return

    setIsSubmitting(true)
    try {
      await api.delete(`/api/organizations/${organization.id}/members/${selectedMember.id}`)
      toast({
        title: 'Member removed',
        description: `${selectedMember.name} has been removed from the team`
      })
      setShowDeleteDialog(false)
      setSelectedMember(null)
      loadTeam()
    } catch (error: any) {
      toast({
        title: 'Failed to remove member',
        description: error.response?.data?.message || 'Something went wrong',
        variant: 'destructive'
      })
    } finally {
      setIsSubmitting(false)
      // Always refresh — member may already be removed on backend
      loadTeam()
    }
  }

  // User Account handlers
  const handleAddUser = async () => {
    if (formData.password !== confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive'
      })
      return
    }

    if (formData.password.length < 8) {
      toast({
        title: 'Error',
        description: 'Password must be at least 8 characters',
        variant: 'destructive'
      })
      return
    }

    try {
      const response = await api.post('/api/admin/users', formData)

      if (response.data.success) {
        toast({
          title: 'Success',
          description: `User ${formData.name} created successfully`,
        })

        setIsAddDialogOpen(false)
        setFormData({ email: '', password: '', name: '', role: 'agent', department: '' })
        setConfirmPassword('')
        setShowPassword(false)
        setShowConfirmPassword(false)
        fetchUsers()
      }
    } catch (error: any) {
      console.error('Error creating user:', error)
      const message = error?.response?.data?.message || 'Failed to create user'
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive'
      })
    }
  }

  const handleUpdateUser = async () => {
    if (!selectedUser) return

    try {
      const response = await api.put(`/api/admin/users/${selectedUser.id}`, {
        name: formData.name,
        email: formData.email,
        department: formData.department
      })

      if (response.data.success) {
        toast({
          title: 'Success',
          description: 'User updated successfully'
        })

        setIsEditUserDialogOpen(false)
        setSelectedUser(null)
        fetchUsers()
      }
    } catch (error) {
      console.error('Error updating user:', error)
      toast({
        title: 'Error',
        description: 'Failed to update user',
        variant: 'destructive'
      })
    }
  }

  const handleDeleteUser = async () => {
    if (!selectedUser) return

    try {
      const response = await api.delete(`/api/admin/users/${selectedUser.id}`)

      if (response.data.success) {
        toast({
          title: 'Success',
          description: 'User deleted successfully'
        })

        setIsDeleteUserDialogOpen(false)
        setSelectedUser(null)
        fetchUsers()
      }
    } catch (error) {
      console.error('Error deleting user:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete user',
        variant: 'destructive'
      })
    }
  }

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return
    if (newPassword.length < 8) {
      toast({ title: 'Error', description: 'Password must be at least 8 characters', variant: 'destructive' })
      return
    }
    if (confirmPassword && newPassword !== confirmPassword) {
      toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' })
      return
    }

    try {
      const response = await api.put(`/api/admin/users/${selectedUser.id}/reset-password`, {
        newPassword
      })

      if (response.data.success) {
        toast({
          title: 'Success',
          description: `Password reset successfully for ${selectedUser.name}`
        })

        setIsResetPasswordDialogOpen(false)
        setSelectedUser(null)
        setNewPassword('')
        setShowNewPassword(false)
      }
    } catch (error: any) {
      console.error('Error resetting password:', error)
      const message = error?.response?.data?.message || 'Failed to reset password'
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive'
      })
    }
  }

  const handleChangeRole = async (userId: string, newRole: string) => {
    try {
      const response = await api.put(`/api/admin/users/${userId}/role`, {
        role: newRole
      })

      if (response.data.success) {
        toast({
          title: 'Success',
          description: 'User role updated successfully'
        })

        fetchUsers()
      }
    } catch (error) {
      console.error('Error changing role:', error)
      toast({
        title: 'Error',
        description: 'Failed to change role',
        variant: 'destructive'
      })
    }
  }

  // Helper functions
  const roleLabels: Record<string, string> = { admin: 'Admin', supervisor: 'Supervisor', agent: 'Team Member' }

  const getRoleBadge = (role: string, member?: any) => {
    const variants: Record<string, { variant: 'default' | 'secondary' | 'outline'; icon: React.ReactNode }> = {
      admin: { variant: 'default', icon: <ShieldCheck className="h-3 w-3 mr-1" /> },
      supervisor: { variant: 'secondary', icon: <Shield className="h-3 w-3 mr-1" /> },
      agent: { variant: 'outline', icon: null }
    }
    const config = variants[role] || { variant: 'outline' as const, icon: null }
    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-1.5">
        <Badge variant={config.variant} className="w-fit">
          {config.icon}
          {roleLabels[role] || role}
        </Badge>
        {member?.isSuperAdmin && (
          <Badge className="w-fit border-transparent bg-[#ede9fe] dark:bg-violet-900/40 text-violet-600 dark:text-violet-300 shadow-sm">
            <ShieldCheck className="h-3 w-3 mr-1" />
            Super Admin
          </Badge>
        )}
        {member?.relationship === 'agency_partner' && (
          <Badge className="w-fit border-transparent bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300">
            Agency Partner
          </Badge>
        )}
      </div>
    )
  }

  const getStatusBadge = (status: string) => {
    return (
      <Badge variant={status === 'active' || status === 'online' ? 'default' : 'secondary'} className="capitalize">
        {status}
      </Badge>
    )
  }

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const filteredMembers = members.filter((member) => {
    if (statusFilter === 'online' && member.status !== 'online') return false
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      member.name.toLowerCase().includes(query) ||
      member.email.toLowerCase().includes(query)
    )
  })

  const canAddMore = organization ? members.length < organization.limits.agents : false

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-muted animate-pulse rounded" />
        <div className="h-96 bg-muted animate-pulse rounded-lg" />
      </div>
    )
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      <PageHeader
        eyebrow="Organization"
        title="Team & Users"
        subtitle={statusFilter === 'online' ? 'Showing online team members' : "Manage your organization's team members and user accounts."}
        icon={
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-brand-royal/20 to-brand-periwinkle/15 border border-brand-royal/20 flex items-center justify-center">
            <Users2 className="h-5 w-5 text-brand-royal" />
          </div>
        }
      />

      {statusFilter && (
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-primary/10 border border-primary/20">
          <div className="flex items-center gap-2 text-sm font-medium text-primary">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
            </span>
            Filtered: showing {statusFilter} members only
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto h-7 text-primary hover:text-primary hover:bg-primary/10"
            onClick={() => { searchParams.delete('filter'); setSearchParams(searchParams) }}
          >
            <X className="w-3.5 h-3.5 mr-1" />
            Show All
          </Button>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="members" className="space-y-6" onValueChange={(v) => setActiveTab(v)}>
        <TabsList>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="invitations" className="relative">
            Invitations
            {activeTab !== 'invitations' && invitations.filter(i => i.status === 'pending').length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center h-5 min-w-[20px] px-1 text-xs font-medium bg-primary text-primary-foreground rounded-full">
                {invitations.filter(i => i.status === 'pending').length}
              </span>
            )}
          </TabsTrigger>
          {(agent as any)?.isPlatformAdmin && (
            <TabsTrigger value="users">User Accounts</TabsTrigger>
          )}
        </TabsList>

        {/* Members Tab */}
        <TabsContent value="members" className="space-y-6">
          {/* Action Button */}
          <div className="flex justify-end">
            <Button onClick={() => setShowInviteDialog(true)} disabled={!canAddMore}>
              <UserPlus className="mr-2 h-4 w-4" />
              Invite Member
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Members</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{statusFilter ? `${filteredMembers.length} / ${members.length}` : members.length}</div>
                {organization && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {statusFilter ? `${members.length} total • ` : ''}{organization.limits.agents - members.length} seats remaining
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Admins</CardTitle>
                <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {members.filter((m) => m.role === 'admin').length}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Full access to all features
                </p>
              </CardContent>
            </Card>

            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Now</CardTitle>
                <div className="h-2 w-2 bg-green-500 rounded-full" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {members.filter((m) => m.status === 'online').length}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Currently online
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Search */}
          <Card className="card-inset">
            <CardContent className="pt-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  aria-label="Search team members"
                />
              </div>
            </CardContent>
          </Card>

          {/* Team Table */}
          <Card className="card-inset">
            <CardContent className="p-0">
              {/* Mobile card list — replaces the horizontal-scroll table on phones */}
              <div className="sm:hidden divide-y">
                {filteredMembers.length === 0 ? (
                  <div className="py-12 flex flex-col items-center justify-center text-center px-4">
                    <Users className="w-12 h-12 text-muted-foreground/40 mb-3" />
                    <p className="text-muted-foreground font-medium">No team members yet</p>
                    <p className="text-muted-foreground/60 text-sm mt-1">
                      Invite your team using the button above to get started
                    </p>
                  </div>
                ) : (
                  filteredMembers.map((member) => (
                    <MobileListItem
                      key={member.id}
                      iconNode={
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={member.avatar} />
                          <AvatarFallback>{getInitials(member.name)}</AvatarFallback>
                        </Avatar>
                      }
                      title={member.name}
                      subtitle={member.email}
                      meta={member.lastActive && `Active ${format(new Date(member.lastActive), 'MMM d, h:mm a')}`}
                      status={getStatusBadge(member.status)}
                      actions={
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-9 w-9">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => {
                              setSelectedMember(member)
                              setShowEditDialog(true)
                            }}>
                              <Edit className="mr-2 h-4 w-4" />
                              Edit Role
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => window.open(`mailto:${member.email}`, '_blank')}>
                              <Mail className="mr-2 h-4 w-4" />
                              Send Email
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/messages?contact=${member.id}`)}>
                              <MessageSquare className="mr-2 h-4 w-4" />
                              Team Message
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => {
                                setSelectedMember(member)
                                setShowDeleteDialog(true)
                              }}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      }
                    >
                      <div className="flex flex-wrap items-center gap-1.5">
                        {getRoleBadge(member.role, member)}
                      </div>
                    </MobileListItem>
                  ))
                )}
              </div>

              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto scrollbar-thin">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden md:table-cell">Last Active</TableHead>
                    <TableHead className="hidden md:table-cell">Joined</TableHead>
                    <TableHead className="w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMembers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-16">
                        <div className="flex flex-col items-center justify-center text-center">
                          <Users className="w-12 h-12 text-muted-foreground/40 mb-3" />
                          <p className="text-muted-foreground font-medium">No team members yet</p>
                          <p className="text-muted-foreground/60 text-sm mt-1">
                            Invite your team using the button above to get started
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredMembers.map((member) => (
                      <TableRow key={member.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar>
                              <AvatarImage src={member.avatar} />
                              <AvatarFallback>{getInitials(member.name)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{member.name}</p>
                              <p className="text-sm text-muted-foreground">{member.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{getRoleBadge(member.role, member)}</TableCell>
                        <TableCell>{getStatusBadge(member.status)}</TableCell>
                        <TableCell className="hidden md:table-cell">
                          {member.lastActive
                            ? format(new Date(member.lastActive), 'MMM d, yyyy h:mm a')
                            : 'No recorded activity'}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {format(new Date(member.createdAt), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => {
                                setSelectedMember(member)
                                setShowEditDialog(true)
                              }}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit Role
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => window.open(`mailto:${member.email}`, '_blank')}>
                                <Mail className="mr-2 h-4 w-4" />
                                Send Email
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/messages?contact=${member.id}`)}>
                                <MessageSquare className="mr-2 h-4 w-4" />
                                Team Message
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => {
                                  setSelectedMember(member)
                                  setShowDeleteDialog(true)
                                }}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invitations Tab */}
        <TabsContent value="invitations" className="space-y-6">
          <Card className="card-inset">
            <CardHeader>
              <CardTitle className="text-lg">Pending & Recent Invitations</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {invitationsLoading ? (
                <div className="p-8 text-center text-muted-foreground">Loading invitations...</div>
              ) : invitations.length === 0 ? (
                <div className="py-16 flex flex-col items-center justify-center text-center">
                  <Mail className="w-12 h-12 text-muted-foreground/40 mb-3" />
                  <p className="text-muted-foreground font-medium">No invitations sent yet</p>
                  <p className="text-muted-foreground/60 text-sm mt-1">
                    Use the "Invite Member" button to invite team members
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Invited By</TableHead>
                      <TableHead>Sent</TableHead>
                      <TableHead className="w-[120px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invitations.map((invite) => (
                      <TableRow key={invite.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{invite.email}</span>
                          </div>
                        </TableCell>
                        <TableCell>{getRoleBadge(invite.role)}</TableCell>
                        <TableCell>
                          {invite.status === 'pending' && (
                            <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                              <Clock className="h-3 w-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                          {invite.status === 'accepted' && (
                            <Badge variant="default" className="bg-green-600">
                              Accepted
                            </Badge>
                          )}
                          {invite.status === 'expired' && (
                            <Badge variant="secondary">
                              Expired
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {invite.invitedBy || 'Unknown'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(invite.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {invite.status === 'pending' && (
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleResendInvite(invite.id)}
                                title="Resend invitation"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => handleCancelInvite(invite.id)}
                                title="Cancel invitation"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                          {invite.status === 'accepted' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => handleCancelInvite(invite.id)}
                              title="Remove invitation record"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                          {invite.status === 'expired' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8"
                              onClick={() => handleResendInvite(invite.id)}
                            >
                              <RefreshCw className="h-3 w-3 mr-1" />
                              Resend
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* User Accounts Tab */}
        <TabsContent value="users" className="space-y-6">
          {/* Action Button */}
          <div className="flex justify-end">
            <Button onClick={() => setIsAddDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add User
            </Button>
          </div>

          {/* Filters */}
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  className="pl-10 max-w-sm"
                />
              </div>
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="supervisor">Supervisor</SelectItem>
                <SelectItem value="agent">Team Member</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Users Table */}
          <Card className="card-inset">
            <CardContent className="p-0 overflow-x-auto scrollbar-thin">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead className="hidden sm:table-cell">Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="hidden md:table-cell">Department</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden md:table-cell">Last Active</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usersLoading ? (
                    <>
                      {[...Array(3)].map((_, i) => (
                        <TableRow key={i}>
                          <TableCell><div className="h-4 w-32 bg-muted animate-pulse rounded" /></TableCell>
                          <TableCell><div className="h-4 w-40 bg-muted animate-pulse rounded" /></TableCell>
                          <TableCell><div className="h-4 w-16 bg-muted animate-pulse rounded" /></TableCell>
                          <TableCell><div className="h-4 w-20 bg-muted animate-pulse rounded" /></TableCell>
                          <TableCell><div className="h-4 w-16 bg-muted animate-pulse rounded" /></TableCell>
                          <TableCell><div className="h-4 w-24 bg-muted animate-pulse rounded" /></TableCell>
                          <TableCell><div className="h-4 w-8 bg-muted animate-pulse rounded" /></TableCell>
                        </TableRow>
                      ))}
                    </>
                  ) : users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="py-16">
                        <div className="flex flex-col items-center justify-center text-center">
                          <div className="h-16 w-16 rounded-2xl bg-primary/5 flex items-center justify-center mb-4">
                            <Users className="w-8 h-8 text-primary/40" />
                          </div>
                          <p className="font-semibold text-foreground/80">No users found</p>
                          <p className="text-muted-foreground text-sm mt-1">Invite team members to collaborate on leads and conversations.</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="shrink-0">
                              <AvatarImage src={user.avatarUrl} alt={user.name} />
                              <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <span className="font-medium block truncate">{user.name}</span>
                              <span className="text-xs text-muted-foreground block truncate sm:hidden">{user.email}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">{user.email}</TableCell>
                        <TableCell>{getRoleBadge(user.role, user)}</TableCell>
                        <TableCell className="hidden md:table-cell">{user.department || '-'}</TableCell>
                        <TableCell>{getStatusBadge(user.status)}</TableCell>
                        <TableCell className="hidden md:table-cell">
                          {user.lastActive ? new Date(user.lastActive).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedUser(user)
                                  setFormData({
                                    email: user.email,
                                    password: '',
                                    name: user.name,
                                    role: user.role,
                                    department: user.department || ''
                                  })
                                  setIsEditUserDialogOpen(true)
                                }}
                              >
                                <Pencil className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedUser(user)
                                  setNewPassword('')
                                  setIsResetPasswordDialogOpen(true)
                                }}
                              >
                                <KeyRound className="w-4 h-4 mr-2" />
                                Reset Password
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedUser(user)
                                  setIsDeleteUserDialogOpen(true)
                                }}
                                className="text-red-600"
                                disabled={user.id === agent?.id}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuLabel>Change Role</DropdownMenuLabel>
                              <DropdownMenuItem
                                onClick={() => handleChangeRole(user.id, 'admin')}
                                disabled={user.role === 'admin' || user.id === agent?.id}
                              >
                                <Shield className="w-4 h-4 mr-2" />
                                Admin
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleChangeRole(user.id, 'supervisor')}
                                disabled={user.role === 'supervisor'}
                              >
                                Supervisor
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleChangeRole(user.id, 'agent')}
                                disabled={user.role === 'agent'}
                              >
                                Team Member
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invite Member Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={(open) => {
        setShowInviteDialog(open)
        if (!open) setInviteForm({ email: '', name: '', role: 'agent' })
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to add a new member to your team
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="John Doe"
                maxLength={100}
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="john@example.com"
                maxLength={254}
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={inviteForm.role}
                onValueChange={(value: 'admin' | 'supervisor' | 'agent') =>
                  setInviteForm({ ...inviteForm, role: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agent">Team Member</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleInviteMember} disabled={isSubmitting || !inviteForm.email}>
              {isSubmitting ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Member Role</DialogTitle>
            <DialogDescription>
              Change the role for {selectedMember?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Role</Label>
            <Select
              value={selectedMember?.role}
              onValueChange={(value) => {
                if (selectedMember) {
                  handleUpdateRole(selectedMember.id, value)
                  setShowEditDialog(false)
                }
              }}
            >
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="agent">Team Member</SelectItem>
                <SelectItem value="supervisor">Supervisor</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Member Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Team Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove {selectedMember?.name} from your team? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRemoveMember} disabled={isSubmitting}>
              {isSubmitting ? 'Removing...' : 'Remove Member'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>Create a new user account</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="user-name">Name</Label>
              <Input
                id="user-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-email">Email</Label>
              <Input
                id="user-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-password">Password</Label>
              <div className="relative">
                <Input
                  id="user-password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Min 8 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="user-confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  className={confirmPassword && formData.password !== confirmPassword ? 'border-red-500' : ''}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {confirmPassword && formData.password !== confirmPassword && (
                <p className="text-sm text-red-500">Passwords do not match</p>
              )}
              {confirmPassword && formData.password === confirmPassword && formData.password.length >= 6 && (
                <p className="text-sm text-green-500">Passwords match</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-role">Role</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agent">Team Member</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-department">Department (Optional)</Label>
              <Input
                id="user-department"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsAddDialogOpen(false)
              setConfirmPassword('')
              setShowPassword(false)
              setShowConfirmPassword(false)
            }}>Cancel</Button>
            <Button
              onClick={handleAddUser}
              disabled={!formData.name || !formData.email || formData.password.length < 6 || formData.password !== confirmPassword}
            >
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditUserDialogOpen} onOpenChange={setIsEditUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-user-name">Name</Label>
              <Input
                id="edit-user-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-user-email">Email</Label>
              <Input
                id="edit-user-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-user-department">Department</Label>
              <Input
                id="edit-user-department"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditUserDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateUser}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={isDeleteUserDialogOpen} onOpenChange={setIsDeleteUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedUser?.name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteUserDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteUser}>Delete User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={isResetPasswordDialogOpen} onOpenChange={setIsResetPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {selectedUser?.name} ({selectedUser?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="Enter new password (min 8 characters)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {newPassword && newPassword.length < 6 && (
                <p className="text-sm text-amber-500">Password must be at least 6 characters</p>
              )}
              {newPassword && newPassword.length >= 6 && (
                <p className="text-sm text-green-500">Password meets requirements</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsResetPasswordDialogOpen(false)
              setNewPassword('')
              setShowNewPassword(false)
            }}>Cancel</Button>
            <Button
              onClick={handleResetPassword}
              disabled={newPassword.length < 6}
            >
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile FAB — primary action */}
      {canAddMore && (
        <MobileFAB
          icon={UserPlus}
          label="Invite member"
          onClick={() => setShowInviteDialog(true)}
        />
      )}
    </div>
  )
}
